import { TriangleAlert } from 'lucide-react';
import type { ComparativoCustoResponse } from '@triade/contracts';
import { FaixaLucroBadge } from '@/components/profit-badge';
import { formatarFracaoPercentual, formatarPontos } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Custo na epoca x hoje (E12). A tela NAO calcula nada: queda, X%, Y%, limiar e faixas vem da API; aqui so se formata.
 * A presenca dos numeros e decidida pelas chaves da resposta (a API so as envia com `custo.ver`), nao pelo papel.
 */

/** Tem os numeros do comparativo (ORCAMENTISTA, FINANCEIRO, ADMIN...). VENDEDOR e LEITURA recebem so as faixas. */
export function temNumerosCusto(c: ComparativoCustoResponse): c is ComparativoCustoResponse & { noEnvio: string; hoje: string; quedaPp: string } {
  return c.noEnvio !== undefined && c.hoje !== undefined && c.quedaPp !== undefined;
}

function AvisoCustos() {
  return (
    <span role="status" data-testid="aviso-custos-mudaram" className="inline-flex items-center gap-1.5 font-medium text-warning-ink">
      <TriangleAlert aria-hidden="true" className="size-4" />
      Custos mudaram desde o envio
    </span>
  );
}

/**
 * Chip "no envio X% · hoje Y%" com a queda em pp e o limiar. Destaque quando `acimaDoLimiar`. Sem os numeros (VENDEDOR,
 * LEITURA): so a mudanca de cor (faixa do envio e de hoje) e o aviso. Quem chama so renderiza com `comparativoCusto` presente.
 */
export function ChipCustoEpoca({ comparativo: c }: { comparativo: ComparativoCustoResponse }) {
  const aviso = c.acimaDoLimiar || c.mudouDeCor;

  if (!temNumerosCusto(c)) {
    return (
      <div data-testid="custo-epoca-cor" className="flex flex-wrap items-center gap-2 text-small">
        {c.faixaNoEnvio && (
          <>
            <span className="text-ink-muted">No envio</span>
            <FaixaLucroBadge faixa={c.faixaNoEnvio} />
          </>
        )}
        {c.faixaHoje && (
          <>
            <span className="text-ink-muted">hoje</span>
            <FaixaLucroBadge faixa={c.faixaHoje} />
          </>
        )}
        {aviso && <AvisoCustos />}
      </div>
    );
  }

  return (
    <div
      data-testid="chip-custo-epoca"
      data-acima={c.acimaDoLimiar ? 'sim' : 'nao'}
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border px-3 py-2 text-small',
        c.acimaDoLimiar ? 'border-warning-dot bg-warning-bg text-warning-ink' : 'border-border bg-line-soft text-ink-body',
      )}
    >
      <span className="tabular font-semibold" data-testid="chip-custo-valores">
        no envio {formatarFracaoPercentual(c.noEnvio)} · hoje {formatarFracaoPercentual(c.hoje)}
      </span>
      <span className="tabular" data-testid="chip-custo-queda">
        queda de {formatarPontos(c.quedaPp)} pp
      </span>
      <span className="text-caption text-ink-muted" data-testid="chip-custo-limiar">
        limite {c.limiarPp != null ? `${formatarPontos(c.limiarPp)} pp` : 'sem parâmetros vigentes'}
      </span>
      {c.itensSemCustoHoje ? (
        <span className="text-caption text-ink-muted">{c.itensSemCustoHoje} item(ns) sem custo hoje: mantêm o custo do envio</span>
      ) : null}
      {aviso && <AvisoCustos />}
    </div>
  );
}

/** Indicador do rascunho: `aoVivo` true = recalculado agora com os custos de hoje (sem gravar); false = valores gravados. */
export function IndicadorAoVivo({ aoVivo }: { aoVivo: boolean }) {
  return aoVivo ? (
    <span data-testid="indicador-ao-vivo" data-ao-vivo="sim" title="Recalculado agora com os custos de hoje. Nada foi gravado." className="rounded-full bg-success-bg px-2.5 py-0.5 text-caption font-semibold text-success-ink">
      ao vivo
    </span>
  ) : (
    <span data-testid="indicador-ao-vivo" data-ao-vivo="nao" className="rounded-full border border-border px-2.5 py-0.5 text-caption text-ink-muted">
      valores gravados (cadastro incompleto)
    </span>
  );
}
